import React, { Fragment, memo, ReactNode, useState } from 'react';

import {
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Center,
  Portal,
  MenuOptionGroup,
  MenuItemOption,
  HStack,
  Stack,
  Divider,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import map from 'lodash/map';
import { customTheme } from 'theme';

import { getIcon } from 'components/Icons/Components';

import { defaultStyles } from './styles';
import { IMenuItemOption, IMenuItemProps, IMenuProps } from './types';

export const CustomMenu = memo((props: IMenuProps) => {
  const {
    menuItems,
    buttonLabel,
    menuTransition = true,
    itemsComponent,
    menuItemOptions,
    styles,
    ...rest
  } = props;

  const [selected, setSelected] = useState<Record<string, any>>({});

  // index comes from lodash map; it keys the fragment when items are listed.
  const menuItem = (item: IMenuItemProps, index?: number) => {
    const { label, divider, icon, iconType, itemStyles, ...rest } = item;
    const props = {
      ...defaultStyles?.item,
      icon: (
        <Center {...defaultStyles?.icon} {...styles?.icon}>
          {!!iconType ? getIcon(iconType) : icon}
        </Center>
      ),
      ...rest,
      ...styles?.item,
      ...itemStyles,
    };
    return (
      <Fragment key={`${item?.key ?? index}-menu-item`}>
        <Box px={2}>
          <MenuItem {...props}>{label}</MenuItem>
        </Box>
        {divider && <MenuDivider />}
      </Fragment>
    );
  };

  const menuItemOption = (item: IMenuItemOption, groupKey: string) => {
    return (
      // Keyed so option lists don't trigger React key warnings.
      <MenuItemOption
        key={`${groupKey}-${item.value}`}
        iconSpacing={4}
        pl={5}
        fontSize="15px"
        {...defaultStyles?.itemOption}
        {...item}
      >
        {item.label}
      </MenuItemOption>
    );
  };

  const renderTitle = (title: any, icon?: ReactNode) => (
    <HStack spacing={3} alignItems="center">
      {icon && <Box boxSize={5}>{icon}</Box>}
      <Box pt={!!icon ? 1 : 0}>{title}</Box>
    </HStack>
  );

  const menuList = () => (
    <MenuList borderRadius={12} {...styles?.list}>
      {itemsComponent
        ? itemsComponent
        : !isEmpty(menuItemOptions)
        ? map(menuItemOptions, (each, index) => (
            <Box key={`${index}-group-option`}>
              <MenuOptionGroup
                {...styles?.optionsGroup}
                {...each?.groupProps}
                onChange={value => {
                  const options = { ...selected, [each.key]: value };
                  setSelected(options);
                  each?.groupProps?.onChange?.(value);
                }}
              >
                {map(each.items, o => menuItemOption(o, each?.key))}
                {menuItemOptions?.length !== index + 1 && (
                  <Box px={3}>
                    <MenuDivider />
                  </Box>
                )}
              </MenuOptionGroup>
            </Box>
          ))
        : map(menuItems, (each, index) => (
            <Box key={`${each?.key ?? index}-item`}>
              {each.group ? (
                <>
                  <Stack spacing={0} {...styles?.group}>
                    {(each?.group?.title || each?.label) && (
                      <Box px={3}>
                        <Box
                          py={1}
                          fontSize={styles?.group?.fontSize ?? 'xs'}
                          color={
                            styles?.group?.color ??
                            customTheme.colors.gray['300']
                          }
                          fontWeight={styles?.group?.fontWeight ?? 600}
                        >
                          {renderTitle(
                            each?.label ?? each?.group?.title,
                            each?.icon,
                          )}
                        </Box>
                      </Box>
                    )}
                    {map(each.group.list, menuItem)}
                    {each.group?.divider && (
                      <Box
                        borderBottom="1px solid"
                        borderColor={customTheme.colors.gray['200']}
                        mb={2}
                      />
                    )}
                  </Stack>

                  {/*<MenuGroup {...styles?.group} {...each?.group}>
                    {map(each.group.list, menuItem)}
                  </MenuGroup>*/}
                  {each?.divider && <MenuDivider />}
                </>
              ) : each?.optionsGroup ? (
                <MenuOptionGroup {...each?.optionsGroup} title={undefined}>
                  <Stack px={3} color="primary">
                    <Box>
                      {renderTitle(
                        each?.label ?? each?.optionsGroup?.title,
                        each?.icon,
                      )}
                    </Box>
                    <Divider borderColor="primary" />
                  </Stack>
                  {map(each.optionsGroup.list, o => menuItemOption(o, o?.key))}
                </MenuOptionGroup>
              ) : (
                menuItem(each)
              )}
            </Box>
          ))}
    </MenuList>
  );

  return (
    <Box fontSize="sm">
      <Menu {...styles?.menu} {...rest}>
        {buttonLabel && (
          <MenuButton
            type="button"
            {...styles?.button}
            onClick={e => e.stopPropagation()}
          >
            {buttonLabel}
          </MenuButton>
        )}
        {menuTransition ? <Portal>{menuList()}</Portal> : menuList()}
      </Menu>
    </Box>
  );
});
